import { authPathBlocksTokenRefresh, authPathOmitsBearerToken } from '@/app/core/config/api-core-urls';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '@/app/core/services/auth.service';
import { isTranslationAssetRequest } from '@/app/core/interceptors/translation-asset-request';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    if (isTranslationAssetRequest(request.url)) {
        return next(request);
    }

    const authService = inject(AuthService);
    const router = inject(Router);
    const accessToken = authService.getAccessToken();
    const requestWithAuth =
        accessToken && !authPathOmitsBearerToken(request.url)
            ? request.clone({
                  setHeaders: {
                      Authorization: `Bearer ${accessToken}`
                  }
              })
            : request;

    return next(requestWithAuth).pipe(
        catchError((error: unknown) => {
            if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
                return throwError(() => error);
            }

            if (authPathBlocksTokenRefresh(request.url)) {
                if (authService.hasStoredSession() || accessToken) {
                    authService.clearLocalSession();
                    void router.navigate(['/auth/login']);
                }
                return throwError(() => error);
            }

            if (!authService.hasStoredSession()) {
                if (accessToken) {
                    authService.clearLocalSession();
                    void router.navigate(['/auth/login']);
                }
                return throwError(() => error);
            }

            return authService.refreshAccessToken().pipe(
                switchMap((ok) => {
                    if (!ok) {
                        authService.clearLocalSession();
                        void router.navigate(['/auth/login']);
                        return throwError(() => error);
                    }

                    const newToken = authService.getAccessToken();
                    if (!newToken) {
                        authService.clearLocalSession();
                        void router.navigate(['/auth/login']);
                        return throwError(() => error);
                    }

                    return next(
                        request.clone({
                            setHeaders: { Authorization: `Bearer ${newToken}` }
                        })
                    );
                })
            );
        })
    );
};
